import { useState, useEffect, useRef } from "react";
import { Search, ChevronDown, Check, Building2 } from "lucide-react";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

/**
 * Componente Select per fornitori con ricerca e raggruppamento per tipologia
 */
const FornitoreSelect = ({ 
  fornitori = [], 
  tipologie = [], 
  value, 
  onChange, 
  placeholder = "Scegli un fornitore",
  disabled = false,
  showAllOption = false,  // Mostra opzione "Tutti"
  showNoneOption = false, // Mostra opzione "Nessun fornitore"
  allOptionLabel = "Tutti",
  noneOptionLabel = "Nessun fornitore"
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  
  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Crea mappa tipologie
  const tipologieMap = {};
  tipologie.forEach(t => {
    tipologieMap[t.id] = t.nome;
  });

  // Filtra fornitori per ricerca
  const fornitoriFiltered = fornitori.filter(f => 
    f.ragione_sociale?.toLowerCase().includes(search.toLowerCase())
  );

  // Raggruppa fornitori per tipologia
  const fornitoriGrouped = {};
  fornitoriFiltered.forEach(f => {
    const tipologia = tipologieMap[f.tipologia_id] || "Altro";
    if (!fornitoriGrouped[tipologia]) {
      fornitoriGrouped[tipologia] = [];
    }
    fornitoriGrouped[tipologia].push(f);
  });

  // Ordina gruppi alfabeticamente e fornitori all'interno di ogni gruppo
  const sortedGroups = Object.keys(fornitoriGrouped).sort((a, b) => {
    // "Altro" sempre per ultimo
    if (a === "Altro") return 1;
    if (b === "Altro") return -1;
    return a.localeCompare(b, 'it');
  });

  // Ordina fornitori in ogni gruppo
  sortedGroups.forEach(group => {
    fornitoriGrouped[group].sort((a, b) => 
      (a.ragione_sociale || '').localeCompare(b.ragione_sociale || '', 'it')
    );
  });

  // Trova fornitore selezionato
  const selectedFornitore = fornitori.find(f => f.id === value);
  
  // Display label
  const displayLabel = value === "__all__" ? allOptionLabel 
    : value === "__none__" || value === "" ? (showNoneOption ? noneOptionLabel : placeholder)
    : selectedFornitore?.ragione_sociale || placeholder;

  const handleSelect = (fornitoreId) => {
    onChange(fornitoreId);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !value && "text-muted-foreground"
        )}
        data-testid="fornitore-select-trigger"
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform", open && "rotate-180")} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg" data-testid="fornitore-select-dropdown">
          {/* Search input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca fornitore..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                autoFocus
                data-testid="fornitore-search-input"
              />
            </div>
          </div>

          {/* Lista fornitori raggruppati */}
          <div className="max-h-[300px] overflow-y-auto p-1">
            {/* Opzione Tutti */}
            {showAllOption && !search && (
              <button
                onClick={() => handleSelect("__all__")}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-2 text-sm rounded-sm cursor-pointer font-medium",
                  "hover:bg-accent hover:text-accent-foreground",
                  value === "__all__" && "bg-accent"
                )}
              >
                <span>{allOptionLabel}</span>
                {value === "__all__" && <Check className="h-4 w-4" />}
              </button>
            )}
            
            {/* Opzione Nessun fornitore */}
            {showNoneOption && !search && (
              <button
                onClick={() => handleSelect("__none__")}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-2 text-sm rounded-sm cursor-pointer text-muted-foreground",
                  "hover:bg-accent hover:text-accent-foreground",
                  (value === "__none__" || value === "") && "bg-accent"
                )}
              >
                <span>{noneOptionLabel}</span>
                {(value === "__none__" || value === "") && <Check className="h-4 w-4" />}
              </button>
            )}
            
            {sortedGroups.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Nessun fornitore trovato
              </div>
            ) : (
              sortedGroups.map(gruppo => (
                <div key={gruppo}>
                  {/* Header gruppo */}
                  <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted sticky top-0 z-10">
                    <Building2 className="h-3 w-3" />
                    {gruppo}
                    <span className="text-[10px] font-normal">({fornitoriGrouped[gruppo].length})</span>
                  </div>
                  
                  {/* Fornitori del gruppo */}
                  {fornitoriGrouped[gruppo].map(fornitore => (
                    <button
                      key={fornitore.id}
                      onClick={() => handleSelect(fornitore.id)}
                      className={cn(
                        "flex w-full items-center justify-between px-3 py-2 text-sm rounded-sm cursor-pointer",
                        "hover:bg-accent hover:text-accent-foreground",
                        value === fornitore.id && "bg-accent"
                      )}
                      data-testid={`fornitore-option-${fornitore.id}`}
                    >
                      <span>{fornitore.ragione_sociale}</span>
                      {value === fornitore.id && <Check className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FornitoreSelect;
